import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { StylesProvider, ThemeProvider as MuiThemeProvider, jssPreset } from '@material-ui/core/styles';
import { CssBaseline } from '@material-ui/core';
import { ThemeProvider } from 'styled-components/macro';
import { create } from 'jss';
import { LocalizationProvider } from '@material-ui/pickers';
import DateFnsAdapter from '@material-ui/pickers/adapter/date-fns';
import localePtBR from 'date-fns/locale/pt-BR';
import AsyncStorage from '@react-native-community/async-storage';

import { getAllRequest as getAllNotifications } from './store/modules/notifications/notifications-actions';
import * as authActions from './store/modules/auth/auth-actions';
import * as appActions from './store/modules/app/app-actions';
import * as configurationActions from './store/modules/configuration/configuration-actions';
import * as tenantActions from './store/modules/tenant/tenant-actions';
import { UserInterface } from '@e3labs/isa-data-dictionary';
import { AppStateType } from './store/modules/root-reducer';

import Loading from './components/Loading';
import createThemeApp from './styles/theme';
import GlobalStyles from './styles/global';
import Routes from '~/routes/routes';
import { I18nProvider } from './i18n';
import OneSignal from 'react-onesignal';
import {
  ISA_TENANT_ID,
  ISA_USER,
  ISA_TOKEN,
  ISA_THEME,
  THEMES,
  ISA_LOCALE,
  LOCALES,
  ISA_USER_SELECTED_FOR_CONTEXT
} from './constants';

const jss = create({
  ...jssPreset(),
  insertionPoint: document.getElementById('jss-insertion-point')!
});

const App: React.FC = () => {
  const { theme, locale } = useSelector((state: AppStateType) => state.app);
  const { user } = useSelector((state: AppStateType) => state.auth);
  const configuration = useSelector((state: AppStateType) => state.configuration);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const bootstrapAsync = async () => {
      setIsLoading(true);

      const currentTheme = await AsyncStorage.getItem(ISA_THEME);
      if (currentTheme) {
        dispatch(appActions.setTheme(currentTheme ?? THEMES.DEFAULT));
      }

      const currentLocale = await AsyncStorage.getItem(ISA_LOCALE);
      if (currentLocale) {
        dispatch(appActions.setLanguage(currentLocale ?? LOCALES.PORTUGUESE));
      }

      const user = await AsyncStorage.getItem(ISA_USER);
      const tenantID = await AsyncStorage.getItem(ISA_TENANT_ID);
      const token = await AsyncStorage.getItem(ISA_TOKEN);
      const userSelectedForContext = await AsyncStorage.getItem(ISA_USER_SELECTED_FOR_CONTEXT);

      if (!user || !token || !tenantID) {
        dispatch(authActions.logoutRequest());
        setIsLoading(false);
        return;
      }

      const userParsed: UserInterface = JSON.parse(user);
      const userSelectedForContextParsed: UserInterface = JSON.parse(userSelectedForContext);
      const userObj = userSelectedForContextParsed || userParsed;

      dispatch(
        authActions.loginSucess({
          user: userObj,
          token,
          tenantID
        })
      );

      if (tenantID) {
        dispatch(configurationActions.getRequest());
        dispatch(tenantActions.getTenantRequest());
      }

      OneSignal.init({
        appId: process.env.REACT_APP_ONESIGNAL!
      });

      OneSignal.sendTag('user', userObj?._id + '_' + tenantID);

      OneSignal.on('notificationDisplay', () => {
        dispatch(getAllNotifications());
      });
      dispatch(getAllNotifications());

      setIsLoading(false);
    };

    bootstrapAsync();
  }, [dispatch]);

  const materialTheme = createThemeApp(theme);

  return (
    <>
      <Helmet titleTemplate="%s | iSA Backoffice" defaultTitle="iSA Backoffice" />
      <StylesProvider jss={jss}>
        <LocalizationProvider dateAdapter={DateFnsAdapter} locale={localePtBR}>
          <MuiThemeProvider theme={materialTheme}>
            <ThemeProvider theme={materialTheme}>
              <CssBaseline />
              <GlobalStyles />
              <I18nProvider locale={locale}>
                {isLoading ? <Loading /> : <Routes user={user} configuration={configuration} />}
              </I18nProvider>
            </ThemeProvider>
          </MuiThemeProvider>
        </LocalizationProvider>
      </StylesProvider>
    </>
  );
};

export default App;
